import { useContext, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import AppGlobalsContext from "app/AppGlobalsContext";
import { isAuthenticated, isIdentified } from "app/pages/Auth/authSelectors";
import { fetchProducts } from "app/pages/Listing/productActionCreators";
import { getMembershipProducts } from "app/pages/Listing/productsSelector";
import { getPartnerAuthLayer } from "app/reducers/partnerSelector";
import type { MembershipAdvantage, MembershipOffersColumns } from "../types";
import { MembershipBlock, MembershipBlockProps } from "./MembershipBlock";

type MembershipMarketing = {
	connectedTitle?: string[];
	disconnectedTitle?: string[];
	advantages?: MembershipAdvantage[];
	cta?: { label?: string };
};

type MembershipState = {
	partner?: { marketing?: { membership?: MembershipMarketing } };
};

type MembershipBlockContainerProps = Pick<MembershipBlockProps, "bleed"> & {
	/** Columns of the offers grid on desktop for members. Visitors always get 3. */
	memberColumns?: MembershipOffersColumns;
};

const selectMembershipBlockProps = (state: MembershipState, pathname: string) => {
	const isMerchPage = pathname.includes("/merch");
	const isMember = Boolean(isAuthenticated(state) || isIdentified(state));
	const membership = state.partner?.marketing?.membership;

	return {
		isMember,
		products: getMembershipProducts(isMerchPage)(state),
		title: isMember ? membership?.connectedTitle : membership?.disconnectedTitle,
		advantages: membership?.advantages,
		signupLabel: membership?.cta?.label,
		signupTexts: getPartnerAuthLayer(state).membership,
	};
};

/** Feeds the membership block from the store, and loads the products it offers. */
export const MembershipBlockContainer = ({
	bleed,
	memberColumns = 3,
}: MembershipBlockContainerProps) => {
	const { pathname } = useLocation();
	const props = useSelector(
		(state: MembershipState) => selectMembershipBlockProps(state, pathname),
		shallowEqual
	);
	const dispatch = useDispatch();
	const { shop, productsVisibility, partnerCode } = useContext(AppGlobalsContext);

	useEffect(() => {
		dispatch(fetchProducts({ productsVisibility, shop, partnerCode }));
	}, [dispatch, productsVisibility, shop, partnerCode]);

	const columns = props.isMember ? memberColumns : 3;

	return <MembershipBlock {...props} columns={columns} bleed={bleed} />;
};
